/**
 * Department Edit Page
 * Page for editing existing departments
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import { useSnackbar } from 'notistack';

// Components
import { DepartmentForm } from './components';
import { PageShell } from '../../components';
import { AvisoDeFalhaAoCarregar } from '../../components/AvisoDeFalhaAoCarregar';
import { falhaDeCarga, textoDaFalhaDeCarga, type FalhaDeCarga } from '../../utils/acessoNegado';

// Services
import { departmentService } from '../../services/departmentService';

// Types
import { Department, DepartmentFormData, UpdateDepartmentRequest, SafetyCharacteristicType } from '../../types/department';

const DepartmentEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();

  const [department, setDepartment] = useState<Department | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [falha, setFalha] = useState<FalhaDeCarga | null>(null);

  // Get return URL params
  const companyId = searchParams.get('companyId');

  // Load department data
  const loadDepartment = async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setFalha(null);

    try {
      const departmentData = await departmentService.getDepartmentById(id);
      setDepartment(departmentData);
    } catch (err: any) {
      console.error('Error loading department:', err);
      const falhaAoCarregar = falhaDeCarga(err, 'Não foi possível carregar o setor. Tente de novo.');
      setFalha(falhaAoCarregar);
      // 403 e 404 já têm explicação na própria tela; só a falha genérica ganha toast.
      if (falhaAoCarregar.status !== 403 && falhaAoCarregar.status !== 404) {
        enqueueSnackbar('Erro ao carregar dados do setor', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadDepartment();
  }, [id]);

  // Handle form submission
  const handleSubmit = async (data: DepartmentFormData) => {
    if (!id) return;

    setSaving(true);
    
    try {
      console.log('🔄 Updating department:', { id, data });
      
      // Convert safety characteristics object to array
      const characteristicsArray = Object.entries(data.safetyCharacteristics)
        .filter(([_, enabled]) => enabled)
        .map(([characteristic]) => characteristic as SafetyCharacteristicType);
      
      const requestData: UpdateDepartmentRequest = {
        name: data.name,
        unitId: data.unitId,
        companyId: data.companyId,
        
        location: data.location ?? undefined,
        construction: data.construction ?? undefined,
        ceilingHeight: data.ceilingHeight ?? undefined,
        flooring: data.flooring ?? undefined,
        roofing: data.roofing ?? undefined,
        ventilation: data.ventilation ?? undefined,
        lighting: data.lighting ?? undefined,
        shift: data.shift ?? undefined,
        
        safetyCharacteristics: characteristicsArray,
      };
      
      console.log('📊 Safety characteristics array:', characteristicsArray);
      
      await departmentService.updateDepartment(id, requestData);
      
      console.log('✅ Department updated successfully');
      enqueueSnackbar('Setor atualizado com sucesso!', { variant: 'success' });
      
      // Navigate back with preserved company filter if it exists
      if (companyId) {
        navigate(`/departments?companyId=${companyId}`);
      } else {
        navigate('/departments');
      }
    } catch (error: any) {
      console.error('❌ Error updating department:', error);
      const errorMessage = (error.response?.data?.message || error.message) ?? 'Erro ao atualizar setor';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (companyId) {
      navigate(`/departments?companyId=${companyId}`);
    } else {
      navigate('/departments');
    }
  };

  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Setores', path: companyId ? `/departments?companyId=${companyId}` : '/departments' },
    { label: 'Editar Setor' },
  ];

  // Loading state
  if (loading) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar Setor">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Carregando dados do setor...
          </Typography>
        </Box>
      </PageShell>
    );
  }

  // Registro não carregou (falha ou inexistente)
  if (!department) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar Setor">
        <AvisoDeFalhaAoCarregar
          mensagem={textoDaFalhaDeCarga(falha, 'Setor não encontrado.')}
          rotuloDoBotao="Voltar para setores"
          aoVoltar={handleCancel}
        />
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title={`Editar Setor: ${department.name}`}
      subtitle="Atualize as informações do setor"
    >
      {department.company && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: -2, mb: 3 }}>
          Empresa: {department.company.tradeName || department.company.legalName}
        </Typography>
      )}

      {/* Form Card */}
      <Card>
        <CardContent>
          <DepartmentForm
            mode="edit"
            department={department}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={saving}
          />
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default DepartmentEditPage;